import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { thinkingFrameworkService, ThinkingFrameworkSession } from '../../services/thinkingFrameworkService';
import PageHeader from '../common/PageHeader';
import FrameworkGuide from '../common/FrameworkGuide';
import './ThinkingFrameworks.css'; // Shared styles

interface FiveWhysAnalysisProps {
  onToggleSidebar: () => void;
}

interface FiveWhysData {
  problem: string;
  whys: string[]; // Array of 5 strings
  rootCause: string;
  solution: string;
}

const FiveWhysGuideContent = (
  <div>
    <h3>What is 5 Whys Analysis?</h3>
    <p>The 5 Whys is an iterative interrogative technique used to explore the cause-and-effect relationships underlying a particular problem. The primary goal is to determine the root cause of a defect or problem by repeating the question "Why?".</p>
    
    <h3>How to use it</h3>
    <ol>
      <li><strong>Define the Problem:</strong> Clearly state the problem you are facing.</li>
      <li><strong>Ask "Why?" (1st Why):</strong> Why is this problem happening? Write down the answer.</li>
      <li><strong>Ask "Why?" again (2nd Why):</strong> Why did the reason in step 2 happen?</li>
      <li><strong>Repeat:</strong> Continue asking "Why?" until you reach the root cause (usually by the 5th why, but it can be more or fewer).</li>
      <li><strong>Determine Root Cause:</strong> The final answer is likely the root cause.</li>
      <li><strong>Propose Solution:</strong> Develop a countermeasure or solution to address the root cause.</li>
    </ol>

    <h3>Example</h3>
    <div className="framework-guide-example">
      <p><strong>Problem:</strong> The vehicle will not start.</p>
      <p>1. <strong>Why?</strong> The battery is dead.</p>
      <p>2. <strong>Why?</strong> The alternator is not functioning.</p>
      <p>3. <strong>Why?</strong> The alternator belt has broken.</p>
      <p>4. <strong>Why?</strong> The alternator belt was well beyond its useful service life and not replaced.</p>
      <p>5. <strong>Why?</strong> The vehicle was not maintained according to the recommended service schedule. (Root Cause)</p>
    </div>
  </div>
);

const FiveWhysAnalysis: React.FC<FiveWhysAnalysisProps> = ({ onToggleSidebar }) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<ThinkingFrameworkSession | null>(null);
  const [title, setTitle] = useState('');
  const [data, setData] = useState<FiveWhysData>({
    problem: '',
    whys: ['', '', '', '', ''],
    rootCause: '',
    solution: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (user) {
      if (sessionId) {
        loadSession();
      } else {
        // New session
        setTitle('');
        setData({
          problem: '',
          whys: ['', '', '', '', ''],
          rootCause: '',
          solution: ''
        });
        setLoading(false);
        setShowGuide(true);
      }
    }
  }, [user, sessionId]);

  const loadSession = async () => {
    if (!sessionId || !user) return;

    try {
      setLoading(true);
      setError(null);
      const loadedSession = await thinkingFrameworkService.getSession(sessionId);
      setSession(loadedSession);
      setTitle(loadedSession.title);
      // Ensure data structure is correct (migration for old data if needed)
      const loadedData = loadedSession.framework_data as FiveWhysData;
      setData({
        problem: loadedData.problem || '',
        whys: Array.isArray(loadedData.whys) && loadedData.whys.length >= 5 
          ? loadedData.whys 
          : ['', '', '', '', ''],
        rootCause: loadedData.rootCause || '',
        solution: loadedData.solution || ''
      });
    } catch (err) {
      console.error('Error loading 5 Whys session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const saveSession = useCallback(async (dataToSave: { title?: string; framework_data?: FiveWhysData }) => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      if (sessionId && session) {
        // Update existing session
        const updated = await thinkingFrameworkService.updateSession(sessionId, dataToSave);
        setSession(updated);
      } else {
        // Create new session
        const newSession = await thinkingFrameworkService.createSession({
          framework_type: '5whys',
          title: dataToSave.title || 'Untitled 5 Whys Analysis',
          framework_data: dataToSave.framework_data || data
        });
        setSession(newSession);
        navigate(`/thinking-frameworks/5whys/${newSession.session_id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving 5 Whys session:', err);
      setError('Failed to save session');
    } finally {
      setSaving(false);
    }
  }, [user, sessionId, session, data, navigate]);

  // Debounced auto-save
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const hasContent = title || data.problem || data.whys.some(w => w) || data.rootCause || data.solution;

    if (hasContent) {
      const timer = setTimeout(() => {
        saveSession({ title, framework_data: data });
      }, 2000); // Auto-save after 2 seconds
      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, data]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  const handleDataChange = (field: keyof FiveWhysData, value: string | string[]) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWhyChange = (index: number, value: string) => {
    const newWhys = [...data.whys];
    newWhys[index] = value;
    handleDataChange('whys', newWhys);
  };

  const handleDelete = async () => {
    if (!sessionId) return;
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;

    try {
      setError(null);
      await thinkingFrameworkService.deleteSession(sessionId);
      navigate('/thinking-frameworks');
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  const handleBack = () => {
    navigate('/thinking-frameworks');
  };

  if (loading) {
    return (
      <div className="thinking-framework-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="thinking-framework-header-title">/5-whys</h1>}
        />
        <div className="thinking-framework-content">
          <div className="thinking-framework-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="thinking-framework-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="thinking-framework-header-title">/5-whys</h1>}
        rightActions={
          <div className="thinking-framework-actions">
            {saving && <span className="thinking-framework-saving">Saving...</span>}
            <button className="framework-guide-btn" onClick={() => setShowGuide(true)}>
              💡 Guide
            </button>
            {sessionId && (
              <button className="thinking-framework-delete-btn" onClick={handleDelete}>
                Delete
              </button>
            )}
            <button className="thinking-framework-back-btn" onClick={handleBack}>
              ← Back
            </button>
          </div>
        }
      />
      
      <div className="thinking-framework-content">
        {error && (
          <div className="thinking-framework-error">{error}</div>
        )}

        <div className="thinking-framework-title-section">
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Analysis Title..."
            className="thinking-framework-title-input"
          />
        </div>

        <div className="five-whys-container">
          <div className="five-whys-section problem-section">
            <label>Define the Problem</label>
            <textarea
              value={data.problem}
              onChange={(e) => handleDataChange('problem', e.target.value)}
              placeholder="What is the specific problem you are facing?"
              rows={3}
            />
          </div>

          <div className="five-whys-list">
            {data.whys.map((why, index) => (
              <div key={index} className="five-whys-item">
                <div className="five-whys-number">{index + 1}</div>
                <div className="five-whys-input-wrapper">
                  <label>Why?</label>
                  <input
                    type="text"
                    value={why}
                    onChange={(e) => handleWhyChange(index, e.target.value)}
                    placeholder={`Why did the previous step happen?`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="five-whys-section root-cause-section">
            <label>Root Cause</label>
            <textarea
              value={data.rootCause}
              onChange={(e) => handleDataChange('rootCause', e.target.value)}
              placeholder="What is the fundamental root cause?"
              rows={2}
            />
          </div>

          <div className="five-whys-section solution-section">
            <label>Proposed Solution</label>
            <textarea
              value={data.solution}
              onChange={(e) => handleDataChange('solution', e.target.value)}
              placeholder="How can you fix the root cause?"
              rows={3}
            />
          </div>
        </div>
      </div>

      <FrameworkGuide
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="5 Whys Analysis"
        content={FiveWhysGuideContent}
      />
    </div>
  );
};

export default FiveWhysAnalysis;

